import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const About = () => {
  return (
    <div className="page-container">
      {/* About Header */}
      <section className="about-header">
        <h1 className="display-huge">About Videre Scire</h1>
        <p className="body-large about-subtitle">
          Independent oversight for complex technology transformations
        </p>
      </section>

      {/* Main Content */}
      <section className="about-main-content">
        <div className="about-text-block">
          <p className="body-large about-paragraph">
            Large-scale systems implementations remain among the riskiest investments enterprises make, especially when they are driven by M&A activity. Cost overruns, extended timelines, late discovery of complexity, and weak realization of business value are persistent, structural problems. They are not caused by a lack of technology or capable system integrators (SIs), but by insufficient early truths: internal management politics, lack of detailed process understanding, resistance to change, weak decision-making criteria, and loss of executive control once the project begins.
          </p>
          
          <p className="body-large about-paragraph-emphasis">
            Our advisory business exists to address exactly these challenges.
          </p>
          
          <p className="body-large about-paragraph">
            We help boards, executive teams, and CIOs retain control of large-scale systems projects, from first commitment through execution, without becoming a system integrator and without displacing delivery partners. Our role is independent, lifecycle-spanning, and anchored in making complexity visible early, decisions explicit, and execution predictable.
          </p>
        </div>
      </section>

      {/* Vision & Mission Grid */}
      <section className="vision-mission-grid">
        <div className="vm-card">
          <h2 className="display-medium">Vision</h2>
          <p className="body-large">
            To be the trusted advisor to boards and executive management, providing clarity, independent oversight, and dissenting perspective in technology-driven transformations — ensuring organizations reduce risk, control costs, and overcome cultural and change management barriers in ERP, cybersecurity, and AI adoption.
          </p>
        </div>

        <div className="vm-card">
          <h2 className="display-medium">Mission</h2>
          <p className="body-large">
            Videre Scire exists to guide leaders through complex digital decisions with impartial advice and disciplined oversight.
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section">
        <h2 className="display-large">Our Philosophy</h2>
        <div className="philosophy-content">
          <div className="philosophy-item">
            <h3 className="heading-2">Seeing to Know</h3>
            <p className="body-medium">
              Videre Scire — "to see in order to know" — embodies our approach. True knowledge comes from direct observation, insight, and perception. We help boards and executives see clearly into risks, culture, and systems so decisions are made wisely.
            </p>
          </div>

          <div className="philosophy-item">
            <h3 className="heading-2">Independent Oversight</h3>
            <p className="body-medium">
              We are not system integrators, vendors, or implementation partners. Our independence ensures we provide impartial advice that prioritizes your organization's best interests, not sales quotas or billable hours.
            </p>
          </div>

          <div className="philosophy-item">
            <h3 className="heading-2">Voice of Dissent</h3>
            <p className="body-medium">
              When hard decisions need to be made, we provide the dissenting perspective that helps leadership see risks and alternatives clearly. We act as your advocate when complexity threatens to overwhelm governance.
            </p>
          </div>

          <div className="philosophy-item">
            <h3 className="heading-2">Lifecycle Partnership</h3>
            <p className="body-medium">
              From initial commitment through execution and realization, we remain engaged. Our role is to ensure executive control is never lost, regardless of project phase or external pressure.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2 className="display-large">Ready to Learn More?</h2>
        <p className="body-large">
          Explore our services or get in touch to discuss how we can help your organization navigate complex transformations.
        </p>
        <div className="cta-buttons">
          <Link to="/services" className="btn-primary">
            View Our Services
            <ArrowRight size={20} />
          </Link>
          <Link to="/contact" className="btn-secondary">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
